import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { generateClient } from 'aws-amplify/api';
import { getCurrentUser } from 'aws-amplify/auth';
import { 
  Users, User, Bot, UserPlus, 
  Shield, Sword, ChevronRight,
  AlertCircle, Check, Plus,
  Clock, Swords, Sparkles,
  GraduationCap, Heart, Brain,
  Footprints, ScrollText, Star,
  Dices
} from 'lucide-react';
import { useCharacter } from '../../../contexts/CharacterContext';
import './PartySelection.css';

// Local game module data for development
const LOCAL_GAME_MODULES = {
  'wintermere': {
    id: 'wintermere',
    name: 'Frost and Bones: The Ruins of Castle Wintermere',
    description: 'A perfect first adventure for new adventurers. Explore the frozen ruins of Castle Wintermere, discover its secrets, and face the challenges that await within.',
    loreDescription: 'Once a proud stronghold in the northern reaches, Castle Wintermere now stands frozen in time, its halls echoing with untold stories. As the first snow falls, whispers of ancient magic stir within its walls...',
    recommendedPlayers: 1,
    minLevel: 1,
    maxLevel: 1,
    maxPartySize: 1,
    startingLevel: 1,
    difficulty: 'BEGINNER',
    estimatedPlayTime: "1-2 hours",
    tags: ['Single Player', 'Story Rich', 'Fantasy', 'Beginner Friendly'],
    recommendedClasses: [
      { name: 'Fighter', description: 'Perfect for beginners. Strong in combat with straightforward abilities.' },
      { name: 'Wizard', description: 'Master arcane spells to overcome challenges. Good choice for tactical players.' },
      { name: 'Rogue', description: 'Sneak and solve puzzles. Great for players who like clever solutions.' },
      { name: 'Cleric', description: 'Balance combat and support magic. Reliable choice for new adventurers.' }
    ],
    features: [
      'Perfect First Adventure',
      'Beginner-Friendly Combat',
      'Engaging Story',
      'Simple Puzzle Elements'
    ],
    version: "1.0.0",
    status: 'PUBLISHED'
  }
};

// Class-specific style configurations
const CLASS_CONFIGS = {
  Fighter: {
    icon: <Sword size={24} />,
    color: '#c41e3a',
    description: 'Masters of martial combat, skilled with weapons and armor.'
  },
  Wizard: {
    icon: <Sparkles size={24} />,
    color: '#4169e1',
    description: 'Wielders of arcane magic, commanding powerful spells.'
  },
  Rogue: {
    icon: <Footprints size={24} />,
    color: '#2e8b57',
    description: 'Skilled adventurers, experts in stealth and precision.'
  },
  Cleric: {
    icon: <Star size={24} />,
    color: '#daa520',
    description: 'Divine spellcasters, bringing healing and guidance.'
  }
};

// Helper Components
const ModuleInfoCard = ({ icon: Icon, label, value, description }) => (
  <div className="party-selection__info-card">
    <div className="party-selection__info-card-header">
      <Icon size={20} />
      <span className="party-selection__info-card-label">{label}</span>
    </div>
    <div className="party-selection__info-card-value">{value}</div>
    {description && (
      <div className="party-selection__info-card-description">{description}</div>
    )}
  </div>
);

const CharacterStatDisplay = ({ character }) => {
  const stats = [
    { icon: <Heart size={16} />, value: character.maxHealth, label: 'HP' },
    { icon: <Sword size={16} />, value: character.strength, label: 'STR' },
    { icon: <Shield size={16} />, value: character.armorClass, label: 'AC' },
    { icon: <Brain size={16} />, value: character.intelligence, label: 'INT' },
    { icon: <Footprints size={16} />, value: character.dexterity, label: 'DEX' }
  ];

  return (
    <div className="party-selection__character-stats">
      {stats.map((stat, index) => (
        <div key={index} className="party-selection__character-stat">
          {stat.icon}
          <span className="party-selection__stat-label">{stat.label}</span>
          <span className="party-selection__stat-value">{stat.value}</span>
        </div>
      ))}
    </div>
  );
};

const ClassBadge = ({ characterClass }) => {
  const config = CLASS_CONFIGS[characterClass] || {};
  return (
    <div 
      className="party-selection__class-badge"
      style={{ '--class-color': config.color }}
    >
      {config.icon}
      <span>{characterClass}</span>
    </div>
  );
};

const client = generateClient();

const PartySelection = () => {
  const { gameId } = useParams();
  const navigate = useNavigate();
  const { 
    characters, 
    loading: charactersLoading, 
    error: charactersError, 
    fetchCharacters: loadCharacters 
  } = useCharacter();

  // State declarations
  const [gameModule, setGameModule] = useState(null);
  const [friends, setFriends] = useState([]);
  const [selectedCharacter, setSelectedCharacter] = useState(null);
  const [aiCharacters, setAiCharacters] = useState([]);
  const [invitedFriends, setInvitedFriends] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Debug logging
  useEffect(() => {
    console.log('Current gameModule:', gameModule);
    console.log('Current characters from context:', characters);
    console.log('Current error state:', error || charactersError);
  }, [gameModule, characters, error, charactersError]);

  // Fetch data on component mount
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        await Promise.all([
          fetchGameModule(),
          loadCharacters(),
          fetchFriends()
        ]);
      } catch (err) {
        console.error('Error loading data:', err);
        setError('Failed to load necessary data. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [gameId, loadCharacters]);

  const fetchGameModule = async () => {
    try {
      // First try to fetch from database
      const result = await client.models.GameModule.get({
        id: gameId
      });
      
      if (result) {
        setGameModule(result);
      } else {
        // Fallback to local data
        const localModule = LOCAL_GAME_MODULES[gameId];
        if (localModule) {
          setGameModule(localModule);
        } else {
          throw new Error('Game module not found');
        }
      }
    } catch (error) {
      console.warn('Falling back to local game module data');
      const localModule = LOCAL_GAME_MODULES[gameId];
      if (localModule) {
        setGameModule(localModule);
      } else {
        console.error('Error fetching game module:', error);
        setError('Failed to load game information. Please try again.');
        throw error;
      }
    }
  };

  const fetchFriends = async () => {
    // For now, just set empty friends array
    setFriends([]);
  };

  const validatePartyConfiguration = () => {
    if (!gameModule || !selectedCharacter) return false;

    const totalPartySize = 1 + aiCharacters.length + invitedFriends.length;

    // For single-player modules, only allow the player character
    if (gameModule.maxPartySize === 1) {
      return totalPartySize === 1 && 
        selectedCharacter.level >= gameModule.minLevel && 
        selectedCharacter.level <= gameModule.maxLevel;
    }

    // For multi-player modules
    const isValidSize = totalPartySize <= gameModule.maxPartySize;
    const hasRequiredSize = totalPartySize >= gameModule.recommendedPlayers;
    const meetsLevelRequirement = 
      selectedCharacter.level >= gameModule.minLevel && 
      selectedCharacter.level <= gameModule.maxLevel;

    return isValidSize && hasRequiredSize && meetsLevelRequirement;
  };

  const getRequirementStatus = () => {
    if (!gameModule || !selectedCharacter) return null;

    const totalPartySize = 1 + aiCharacters.length + invitedFriends.length;
    
    return {
      partySize: {
        met: gameModule.maxPartySize === 1 ? 
          totalPartySize === 1 : 
          (totalPartySize >= gameModule.recommendedPlayers && totalPartySize <= gameModule.maxPartySize),
        text: gameModule.maxPartySize === 1 ? 
          'Single player only' : 
          `Party size (${totalPartySize}/${gameModule.recommendedPlayers}-${gameModule.maxPartySize})`
      },
      level: {
        met: selectedCharacter.level >= gameModule.minLevel && 
             selectedCharacter.level <= gameModule.maxLevel,
        text: `Character level (${selectedCharacter.level}/${gameModule.minLevel}-${gameModule.maxLevel})`
      }
    };
  };

  const getAvailableSlots = () => {
    if (!gameModule || gameModule.maxPartySize === 1) return 0;
    return gameModule.maxPartySize - (1 + aiCharacters.length + invitedFriends.length);
  };

  const handleCharacterSelect = (character) => {
    setSelectedCharacter(character);
    // Clear AI characters and invited friends if switching to single-player module
    if (gameModule?.maxPartySize === 1) {
      setAiCharacters([]);
      setInvitedFriends([]);
    }
  };

  const handleAiCharacterToggle = (character) => {
    if (gameModule?.maxPartySize === 1) return;

    setAiCharacters(prev => {
      if (prev.some(c => c.id === character.id)) {
        return prev.filter(c => c.id !== character.id);
      }
      const availableSlots = getAvailableSlots();
      if (availableSlots > 0) {
        return [...prev, character];
      }
      return prev;
    });
  };

  const handleFriendInvite = (friend) => {
    if (gameModule?.maxPartySize === 1) return;

    setInvitedFriends(prev => {
      if (prev.some(f => f.id === friend.id)) {
        return prev.filter(f => f.id !== friend.id);
      }
      const availableSlots = getAvailableSlots();
      if (availableSlots > 0) {
        return [...prev, friend];
      }
      return prev;
    });
  };

  const handleStartGame = async () => {
    if (!selectedCharacter) {
      setError('Please select your character before starting the game.');
      return;
    }

    if (!validatePartyConfiguration()) {
      setError(`Party configuration doesn't meet module requirements. Please check the requirements listed above.`);
      return;
    }

    try {
      const gameState = {
        id: `gs_${Date.now()}`,
        gameModuleId: gameModule.id,
        partyLeaderId: selectedCharacter.id,
        currentScene: 'start',
        state: JSON.stringify({
          partyLeader: selectedCharacter,
          aiCharacters: gameModule.maxPartySize === 1 ? [] : aiCharacters,
          invitedFriends: gameModule.maxPartySize === 1 ? [] : invitedFriends
        })
      };

      navigate(`/play/${gameId}`, { 
        state: { gameStateId: gameState.id, gameState } 
      });
    } catch (err) {
      console.error('Error starting game:', err);
      setError('Failed to start game. Please try again.');
    }
  };

  if (loading || charactersLoading) {
    return (
      <div className="party-selection__loading">
        <div className="party-selection__loading-spinner" />
        <p>Loading party selection...</p>
      </div>
    );
  }

  if (error || charactersError) {
    return (
      <div className="party-selection__error">
        <AlertCircle size={24} />
        {error || charactersError}
      </div>
    );
  }

  const requirements = getRequirementStatus();
  const availableSlots = getAvailableSlots();

  return (
    <div className="party-selection-container">
      <h2 className="party-selection__title">
        {gameModule?.name}
      </h2>

      <div className="party-selection__module-intro">
        <p className="party-selection__description">
          {gameModule?.description}
        </p>
        <div className="party-selection__lore">
          <ScrollText size={20} />
          <p>{gameModule?.loreDescription}</p>
        </div>

        <div className="party-selection__module-features">
          <ModuleInfoCard
            icon={GraduationCap}
            label="Difficulty"
            value="Beginner"
            description="Perfect for new adventurers"
          />
          <ModuleInfoCard
            icon={Clock}
            label="Duration"
            value={gameModule?.estimatedPlayTime}
            description="A perfect short adventure"
          />
          <ModuleInfoCard
            icon={Users}
            label="Players"
            value="Single Player"
            description="Focus on your story"
          />
          <ModuleInfoCard
            icon={Dices}
            label="Level"
            value="Level 1"
            description="Start your journey"
          />
        </div>

        <div className="party-selection__recommended-classes">
          <h3 className="party-selection__section-subtitle">
            <Star size={20} />
            Recommended Classes
          </h3>
          <div className="party-selection__class-cards">
            {gameModule?.recommendedClasses.map(classInfo => (
              <div key={classInfo.name} className="party-selection__class-card">
                <div className="party-selection__class-card-header">
                  {CLASS_CONFIGS[classInfo.name]?.icon}
                  <h4>{classInfo.name}</h4>
                </div>
                <p>{classInfo.description}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="party-selection__features">
          <h3 className="party-selection__section-subtitle">
            <Sparkles size={20} />
            Adventure Features
          </h3>
          <div className="party-selection__feature-list">
            {gameModule?.features.map(feature => (
              <div key={feature} className="party-selection__feature-item">
                <Check size={16} />
                <span>{feature}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {requirements && (
        <div className="party-selection__requirements">
          <h3 className="party-selection__requirements-title">
            Adventure Requirements
          </h3>
          <div className={`party-selection__requirement-item ${requirements.partySize.met ? 'met' : 'unmet'}`}>
            {requirements.partySize.met ? <Check size={16} /> : <AlertCircle size={16} />}
            {requirements.partySize.text}
          </div>
          <div className={`party-selection__requirement-item ${requirements.level.met ? 'met' : 'unmet'}`}>
            {requirements.level.met ? <Check size={16} /> : <AlertCircle size={16} />}
            {requirements.level.text}
          </div>
        </div>
      )}

      <div className="party-selection__section">
        <h3 className="party-selection__section-title">
          <User size={24} />
          Choose Your Hero
        </h3>
        <div className="party-selection__character-grid">
          {characters.length === 0 ? (
            <div className="party-selection__empty">
              <GraduationCap size={48} />
              <h3>Begin Your Adventure!</h3>
              <p>Create your first character to start your journey into Castle Wintermere.</p>
              <Link to="/create-character" className="party-selection__create-link">
                <Plus size={20} />
                Create Your Hero
              </Link>
            </div>
          ) : (
            characters.map(character => (
              <div 
                key={character.id} 
                className={`party-selection__character-card ${
                  selectedCharacter?.id === character.id ? 'selected' : ''
                }`}
                onClick={() => handleCharacterSelect(character)}
              >
                <div className="party-selection__character-header">
                  <div className="party-selection__character-portrait">
                    <User size={32} />
                  </div>
                  <div className="party-selection__character-titles">
                    <h4 className="party-selection__character-name">
                      {character.name}
                    </h4>
                    <ClassBadge characterClass={character.characterClass} />
                  </div>
                </div>
                <CharacterStatDisplay character={character} />
                <div className="party-selection__character-level">
                  <Star size={16} />
                  Level {character.level}
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      <div className="party-selection__party-summary">
        <h3 className="party-selection__party-summary-title">
          <Users size={24} />
          Adventure Party
        </h3>
        <div className="party-selection__summary-item">
          <Shield size={16} />
          Hero: {selectedCharacter ? (
            <div className="party-selection__selected-hero">
              <span>{selectedCharacter.name}</span>
              <ClassBadge characterClass={selectedCharacter.characterClass} />
            </div>
          ) : (
            <span className="party-selection__no-selection">Choose your hero</span>
          )}
        </div>
      </div>

      <button 
        className="party-selection__start-button"
        onClick={handleStartGame}
        disabled={!selectedCharacter || !validatePartyConfiguration()}
      >
        <Sword size={20} />
        Begin Adventure
        <ChevronRight size={20} />
      </button>
    </div>
  );
};

export default PartySelection;